"use client";

import { useState, useEffect, useRef, memo } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import Image from "next/image";
import { NAV_ITEMS, type NavItem } from "./data";
import { GithubIcon } from "./shared";
import { useAuth } from "@/lib/auth";
import { ChevronDown, LayoutDashboard, BarChart3, LogOut } from "lucide-react";

/* ── Static style constants (avoid re-creation every render) ── */

const SCROLLED_HEADER_STYLE: React.CSSProperties = {
  background: "rgba(255,255,255,0.85)",
  backdropFilter: "blur(28px) saturate(1.6)",
  borderBottom: "1px solid rgba(15,23,42,0.06)",
  boxShadow: "0 1px 12px rgba(0,0,0,0.04), 0 1px 0 rgba(15,23,42,0.02) inset",
};
const TRANSPARENT_HEADER_STYLE: React.CSSProperties = {
  background: "rgba(255,255,255,0.4)",
  backdropFilter: "blur(12px) saturate(1.6)",
  borderBottom: "1px solid transparent",
  boxShadow: "none",
};
const DROPDOWN_PANEL_STYLE: React.CSSProperties = {
  background: "var(--bg-card)",
  border: "1px solid var(--border)",
  boxShadow: "0 12px 40px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.04)",
};
const NAV_GROUP_STYLE: React.CSSProperties = {
  background: "rgba(15,23,42,0.02)",
  border: "1px solid rgba(15,23,42,0.04)",
};
const FREE_TRIAL_CTA_STYLE: React.CSSProperties = {
  background: "linear-gradient(135deg, #6C5CE7 0%, #a855f7 100%)",
  color: "#fff",
  padding: "6px 14px",
  borderRadius: "10px",
  boxShadow: "0 2px 8px rgba(108,92,231,0.3), 0 0 0 1px rgba(108,92,231,0.15) inset",
};
const CTA_BUTTON_STYLE: React.CSSProperties = {
  padding: "10px 22px",
  background: "#0F172A",
  boxShadow: "0 1px 6px rgba(15,23,42,0.15), 0 0 0 1px rgba(15,23,42,0.05) inset",
};
const CTA_BUTTON_SCROLLED_STYLE: React.CSSProperties = {
  padding: "8px 18px",
  background: "#0F172A",
  boxShadow: "0 1px 6px rgba(15,23,42,0.15), 0 0 0 1px rgba(15,23,42,0.05) inset",
};
const MOBILE_BACKDROP_STYLE: React.CSSProperties = {
  background: "rgba(255,255,255,0.9)",
  backdropFilter: "blur(20px)",
};
const MOBILE_PANEL_STYLE: React.CSSProperties = {
  background: "var(--bg-card)",
  border: "1px solid var(--border)",
  boxShadow: "0 20px 60px rgba(0,0,0,0.08)",
};
const ACTIVE_DROPDOWN_ITEM_STYLE: React.CSSProperties = {
  color: "var(--mc-violet)",
  background: "rgba(108,92,231,0.06)",
};
const INACTIVE_DROPDOWN_ITEM_STYLE: React.CSSProperties = {
  color: "var(--text-secondary)",
  background: "transparent",
};
const ACTIVE_MOBILE_LINK_STYLE: React.CSSProperties = {
  color: "var(--mc-violet)",
  background: "rgba(108,92,231,0.06)",
};
const INACTIVE_MOBILE_LINK_STYLE: React.CSSProperties = {
  color: "var(--text-secondary)",
  background: "transparent",
};

/* ── Active link detection ─────────────────────────────────── */

function isActive(href: string, pathname: string, hash: string): boolean {
  // Anchor link: active when on the same page with matching hash
  if (href.startsWith("/#")) {
    const anchorId = href.slice(2);
    return pathname === "/" && hash === anchorId;
  }
  // Exact path match
  if (href === pathname) return true;
  // "More" button — never active as a standalone link
  return false;
}

/* ── Dropdown component ────────────────────────────────────── */

const NavDropdown = memo(function NavDropdown({
  item,
  pathname,
  hash,
}: {
  item: NavItem;
  pathname: string;
  hash: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const anyActive = item.dropdown?.some((d) => isActive(d.href, pathname, hash));

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`nav-link flex items-center gap-1 px-4 py-2 rounded-xl text-[13px] font-medium transition-all ${
          anyActive
            ? "text-[var(--text-primary)]"
            : "text-(--text-secondary) hover:text-[var(--text-primary)]"
        }`}
        aria-expanded={open}
        aria-haspopup="true"
      >
        {item.label}
        <ChevronDown
          className="w-3 h-3 transition-transform duration-200"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
        />
      </button>

      {/* Dropdown panel */}
      <div
        className="absolute top-full left-1/2 -translate-x-1/2 mt-2 min-w-[180px] rounded-2xl p-2 transition-all duration-200"
        style={{
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transform: open
            ? "translate(-50%, 0) scale(1)"
            : "translate(-50%, -8px) scale(0.96)",
          ...DROPDOWN_PANEL_STYLE,
        }}
      >
        {item.dropdown?.map((sub) => {
          const active = isActive(sub.href, pathname, hash);
          return (
            <Link
              key={sub.href}
              href={sub.href}
              onClick={() => setOpen(false)}
              className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-[13px] font-medium transition-all"
              style={active ? ACTIVE_DROPDOWN_ITEM_STYLE : INACTIVE_DROPDOWN_ITEM_STYLE}
              onMouseEnter={(e) => {
                if (!active) {
                  (e.currentTarget as HTMLElement).style.background =
                    "rgba(15,23,42,0.03)";
                }
              }}
              onMouseLeave={(e) => {
                if (!active) {
                  (e.currentTarget as HTMLElement).style.background = "transparent";
                }
              }}
            >
              {sub.label}
            </Link>
          );
        })}
      </div>
    </div>
  );
});

/* ── SiteHeader ─────────────────────────────────────────────── */

export default function SiteHeader() {
  const { user, logout } = useAuth();
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hash, setHash] = useState("");
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);

  // Track scroll for header shrink
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Track hash changes (anchor links like /#features)
  useEffect(() => {
    const updateHash = () => setHash(window.location.hash.replace("#", ""));
    updateHash();
    window.addEventListener("hashchange", updateHash);
    return () => window.removeEventListener("hashchange", updateHash);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- legitimate UI state sync on navigation
    setMobileOpen(false);
  }, [pathname]);

  // Close account dropdown on outside click
  useEffect(() => {
    if (!accountOpen) return;
    const handler = (e: MouseEvent) => {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) {
        setAccountOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [accountOpen]);


  return (
    <>
      <header
        className="fixed top-0 inset-x-0 z-50 transition-all duration-500"
        style={{
          height: scrolled ? 56 : 72,
          ...(scrolled ? SCROLLED_HEADER_STYLE : TRANSPARENT_HEADER_STYLE),
        }}
      >
        {/* Animated gradient line at top */}
        <div
          className="absolute top-0 left-0 right-0 h-[1px] overflow-hidden"
          style={{ opacity: scrolled ? 1 : 0, transition: "opacity 0.5s ease" }}
        >
          <div className="header-gradient-line" />
        </div>

        <div className="max-w-[1400px] mx-auto w-full h-full px-6 flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="shrink-0 group flex items-center gap-2 relative">
            <Image
              src="/logo-line.svg"
              alt="MonkeysCode"
              width={560}
              height={130}
              className="h-auto transition-all duration-300"
              style={{ width: scrolled ? 150 : 180 }}
              priority
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center">
            <div
              className="flex items-center gap-0.5 px-2 py-1.5 rounded-2xl"
              style={NAV_GROUP_STYLE}
            >
              {NAV_ITEMS.map((item) =>
                item.dropdown ? (
                  <NavDropdown
                    key={item.href}
                    item={item}
                    pathname={pathname}
                    hash={hash}
                  />
                ) : (
                  item.label === "Start Free" ? (
                    <Link
                      key={item.href}
                      href={item.href}
                      className="nav-link px-4 py-2 rounded-xl text-[13px] font-semibold transition-all hover:brightness-110 hover:shadow-lg"
                      style={FREE_TRIAL_CTA_STYLE}
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <Link
                      key={item.href}
                      href={item.href}
                      className={`nav-link px-4 py-2 rounded-xl text-[13px] font-medium transition-all relative ${
                        isActive(item.href, pathname, hash)
                          ? "text-[var(--text-primary)]"
                          : "text-(--text-secondary) hover:text-[var(--text-primary)]"
                      }`}
                    >
                      {item.label}
                      {/* Active indicator dot */}
                      {isActive(item.href, pathname, hash) && (
                        <span
                          className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full"
                          style={{ background: "var(--mc-violet)" }}
                        />
                      )}
                    </Link>
                  )
                )
              )}
            </div>
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-2">
            <a
              href="https://github.com/MonkeysCodeAI"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub"
              className="hidden sm:flex p-2.5 rounded-xl text-(--text-muted) hover:text-[var(--text-primary)] hover:bg-[rgba(15,23,42,0.04)] transition-all"
            >
              <GithubIcon />
            </a>

            {user ? (
              <div ref={accountRef} className="relative hidden sm:block">
                <button
                  onClick={() => setAccountOpen(!accountOpen)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-[13px] font-medium text-(--text-secondary) hover:text-[var(--text-primary)] hover:bg-[rgba(15,23,42,0.03)] transition-all"
                >
                  Dashboard
                  <ChevronDown
                    className="w-3 h-3 transition-transform duration-200"
                    style={{ transform: accountOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                  />
                </button>
                {accountOpen && (
                  <div
                    className="absolute right-0 top-full mt-2 w-48 rounded-2xl p-2 z-50"
                    style={{
                      ...DROPDOWN_PANEL_STYLE,
                      animation: 'fadeIn 0.15s ease-out',
                    }}
                  >
                    <Link
                      href="/dashboard"
                      onClick={() => setAccountOpen(false)}
                      className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-[13px] font-medium transition-all hover:bg-[rgba(15,23,42,0.03)]"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      <LayoutDashboard className="w-3.5 h-3.5" style={{ color: 'var(--mc-violet)' }} />
                      Dashboard
                    </Link>
                    <Link
                      href="/dashboard/usage"
                      onClick={() => setAccountOpen(false)}
                      className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-[13px] font-medium transition-all hover:bg-[rgba(15,23,42,0.03)]"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      <BarChart3 className="w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />
                      Usage
                    </Link>
                    <div style={{ height: 1, background: 'var(--border)', margin: '4px 8px' }} />
                    <button
                      onClick={() => { setAccountOpen(false); logout(); }}
                      className="flex items-center gap-2.5 w-full px-3.5 py-2.5 rounded-xl text-[13px] font-medium transition-all hover:bg-[rgba(239,68,68,0.04)]"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      <LogOut className="w-3.5 h-3.5" style={{ color: '#ef4444' }} />
                      <span style={{ color: '#ef4444' }}>Sign out</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
              <Link
                href="/login"
                className="hidden sm:flex px-4 py-2 rounded-xl text-[13px] font-medium text-(--text-secondary) hover:text-[var(--text-primary)] hover:bg-[rgba(15,23,42,0.03)] transition-all"
              >
                Sign in
              </Link>
              <Link
                href="/register"
                className="hidden sm:flex px-4 py-2 rounded-xl text-[13px] font-semibold text-white transition-all duration-300 hover:opacity-90 hover:shadow-lg"
                style={{ background: 'linear-gradient(135deg, #6c5ce7, #8b5cf6)', boxShadow: '0 2px 8px rgba(108,92,231,0.3)' }}
              >
                Get Started
              </Link>
              </>
            )}

            {/* Primary CTA */}
            <Link
              href="/download"
              className="relative group overflow-hidden rounded-xl text-[12px] font-semibold text-white transition-all duration-300"
              style={scrolled ? CTA_BUTTON_SCROLLED_STYLE : CTA_BUTTON_STYLE}
            >
              <span className="absolute inset-0 btn-shimmer" />
              <span className="relative flex items-center gap-1.5">
                Download
                <svg className="w-3 h-3 opacity-60 transition-transform group-hover:translate-x-0.5 group-hover:opacity-100" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12l7 7 7-7" /></svg>
              </span>
            </Link>

            {/* Mobile hamburger */}
            <button
              className="lg:hidden p-2 rounded-xl text-(--text-muted) hover:text-[var(--text-primary)] hover:bg-[rgba(15,23,42,0.04)] transition-all"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                {mobileOpen
                  ? <path d="M18 6L6 18M6 6l12 12" />
                  : <path d="M4 6h16M4 12h16M4 18h16" />
                }
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      <div
        className="fixed inset-0 z-40 lg:hidden transition-all duration-400"
        style={{
          opacity: mobileOpen ? 1 : 0,
          pointerEvents: mobileOpen ? "auto" : "none",
        }}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0"
          style={MOBILE_BACKDROP_STYLE}
          onClick={() => setMobileOpen(false)}
        />
        {/* Panel */}
        <nav
          className="absolute top-20 left-4 right-4 rounded-2xl p-6 transition-all duration-300 max-h-[calc(100vh-100px)] overflow-y-auto"
          style={{
            ...MOBILE_PANEL_STYLE,
            transform: mobileOpen ? "translateY(0)" : "translateY(-20px)",
          }}
        >
          {NAV_ITEMS.map((item) => {
            if (item.dropdown) {
              return (
                <div key={item.href} className="mb-2">
                  <div
                    className="px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-(--text-muted)"
                  >
                    {item.label}
                  </div>
                  <div className="ml-2 space-y-0.5">
                    {item.dropdown.map((sub) => {
                      const active = isActive(sub.href, pathname, hash);
                      return (
                        <Link
                          key={sub.href}
                          href={sub.href}
                          onClick={() => setMobileOpen(false)}
                          className="block px-4 py-3 rounded-xl text-[15px] font-medium transition-all"
                          style={active ? ACTIVE_MOBILE_LINK_STYLE : INACTIVE_MOBILE_LINK_STYLE}
                        >
                          {sub.label}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              );
            }
            const active = isActive(item.href, pathname, hash);
            return item.label === "Start Free" ? (
              <Link
                key={item.href}
                href={item.href}
                onClick={() => setMobileOpen(false)}
                className="block px-4 py-3 rounded-xl text-[15px] font-semibold text-center transition-all hover:brightness-110 mt-2"
                style={FREE_TRIAL_CTA_STYLE}
              >
                {item.label}
              </Link>
            ) : (
              <Link
                key={item.href}
                href={item.href}
                onClick={() => setMobileOpen(false)}
                className="block px-4 py-3 rounded-xl text-[15px] font-medium transition-all"
                style={active ? ACTIVE_MOBILE_LINK_STYLE : INACTIVE_MOBILE_LINK_STYLE}
              >
                {item.label}
              </Link>
            );
          })}
          <div className="pt-3 mt-3" style={{ borderTop: "1px solid var(--border)" }}>
            {user ? (
              <>
                <Link
                  href="/dashboard"
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-[15px] font-medium text-(--text-secondary) hover:text-[var(--text-primary)] transition-all"
                  onClick={() => setMobileOpen(false)}
                >
                  <LayoutDashboard className="w-4 h-4" />
                  Dashboard
                </Link>
                <Link
                  href="/dashboard/usage"
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-[15px] font-medium text-(--text-secondary) hover:text-[var(--text-primary)] transition-all"
                  onClick={() => setMobileOpen(false)}
                >
                  <BarChart3 className="w-4 h-4" />
                  Usage
                </Link>
                <button
                  onClick={() => { setMobileOpen(false); logout(); }}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-[15px] font-medium transition-all"
                  style={{ color: '#ef4444' }}
                >
                  <LogOut className="w-4 h-4" />
                  Sign out
                </button>
              </>
            ) : (
              <>
              <Link
                href="/login"
                className="block px-4 py-3 rounded-xl text-[15px] font-medium text-(--text-secondary) hover:text-[var(--text-primary)] transition-all"
                onClick={() => setMobileOpen(false)}
              >
                Sign in
              </Link>
              <Link
                href="/register"
                className="block px-4 py-3 rounded-xl text-[15px] font-semibold text-white text-center transition-all"
                style={{ background: 'linear-gradient(135deg, #6c5ce7, #8b5cf6)', boxShadow: '0 2px 8px rgba(108,92,231,0.3)' }}
                onClick={() => setMobileOpen(false)}
              >
                Get Started
              </Link>
              </>
            )}
          </div>
        </nav>
      </div>
    </>
  );
}
